import { useCallback } from "react";
import type { ChartConfig, TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  ChartTooltip,
  getChartLines,
  MatchCase,
  MobileScaleCard,
  RenderMd,
  Scale,
  YAxisTick,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, useMedia, View, XStack, YStack } from "tamagui";

import { dateToReadable } from "~/utils/date-to-readable";
import type { ScalesData, SlicesScalesData } from "~/hooks";
import { useConstructScales } from "~/hooks";

interface AccordionData {
  id: string;
  title: string;
  state?: "neutral" | "success" | "warning" | "danger";
  content: {
    title?: string;
    description?: string;
    mdText: string;
  };
}

interface ChartData {
  [key: string]: unknown;
  date: string | Date;
  percentage: number;
}

interface ScaleCardContentData {
  accordion: AccordionData[];
  chart: {
    config: ChartConfig;
    data: ChartData[];
  };
}

interface Scales extends ScaleCardContentData {
  id: string;
  score: string | number;
  title: string;
  state: "neutral" | "success" | "critical" | "warning" | "danger";
  stateText: string | undefined;
}

type FormattedScales<T extends ScalesData | SlicesScalesData> = ReturnType<
  typeof useConstructScales<T>
>;

export const MentalBalanceScales: React.FC<{ data: ScalesData }> = ({
  data,
}) => {
  const { sm } = useMedia();
  const { t } = useTranslation("results");
  const scales = useConstructScales(data);

  return (
    <Card
      data-id={"mental-balance-scales-container"}
      gap={"$5"}
      size="sm"
      shadow="none"
      variant="borderless"
      $gtMd={{ shadow: "md", size: "md", variant: "default" }}
    >
      <YStack
        data-id={"mental-balance-scales-header"}
        gap={"$1"}
        alignItems="center"
      >
        <Card.Title data-id={"mental-balance-scales-title"}>
          {t("scalesCard.title")}
        </Card.Title>
        <Card.Subtitle
          data-id={"mental-balance-scales-subtitle"}
          textAlign="center"
        >
          {t("scalesCard.subTitle")}
        </Card.Subtitle>
      </YStack>
      <MatchCase value={sm}>
        {(Case) => (
          <>
            <Case condition={false}>
              <Scales scales={scales} />
            </Case>

            <Case condition={true}>
              <ScalesMobile scales={scales} />
            </Case>
          </>
        )}
      </MatchCase>
    </Card>
  );
};

export const Scales: React.FC<{ scales: FormattedScales<ScalesData> }> = ({
  scales,
}) => {
  return (
    <Scale.Provider>
      <Scale.Container data-id="scales-list-card-container">
        {scales.map(({ id, title, state, stateText, ...rest }) => (
          <Scale.Body id={id} key={id} shadow="md">
            <Scale.ManagerHeader
              title={title}
              state={state ?? "neutral"}
              toogleLabel={"Детальний опис"}
              stateText={stateText}
              score={undefined}
            />
            <ScaleCardContent data={rest} />
          </Scale.Body>
        ))}
      </Scale.Container>
    </Scale.Provider>
  );
};

export const ScalesMobile: React.FC<{
  scales: FormattedScales<ScalesData>;
}> = ({ scales }) => {
  return (
    <MobileScaleCard.Provider>
      <MobileScaleCard.Container>
        {scales.map(({ id, title, state, stateText, ...rest }) => (
          <MobileScaleCard.Body id={id} key={id}>
            <MobileScaleCard.Header
              toogleLabel="Детальний стан"
              stateText={stateText}
              state={state}
              title={title}
              score={undefined}
            />
            <ScaleCardContentMobile data={rest} />
          </MobileScaleCard.Body>
        ))}
      </MobileScaleCard.Container>
    </MobileScaleCard.Provider>
  );
};

export const SliceScales: React.FC<{
  scales: FormattedScales<SlicesScalesData>;
}> = ({ scales }) => {
  return (
    <Scale.Provider>
      <Scale.Container data-id="scales-list-card-container" maxWidth={1200}>
        {scales.map(({ id, title, state, stateText, ...rest }) => (
          <Scale.Body id={id} key={id} shadow="md">
            <Scale.ManagerHeader
              title={title}
              state={state ?? "neutral"}
              toogleLabel={"Детальний опис"}
              stateText={stateText}
              score={undefined}
            />
            <ScaleCardContent data={rest} />
          </Scale.Body>
        ))}
      </Scale.Container>
    </Scale.Provider>
  );
};

export const SliceScalesMobile: React.FC<{
  scales: FormattedScales<SlicesScalesData>;
}> = ({ scales }) => {
  return (
    <Card
      variant="borderless"
      size="md"
      shadow="none"
      width={"100%"}
      $sm={{ size: "sm" }}
    >
      <MobileScaleCard.Provider>
        <MobileScaleCard.Container>
          {scales.map(({ id, title, state, stateText, ...rest }) => (
            <MobileScaleCard.Body id={id} key={id}>
              <MobileScaleCard.Header
                toogleLabel="Детальний стан"
                stateText={stateText}
                state={state}
                title={title}
                score={undefined}
              />
              <ScaleCardContentMobile data={rest} />
            </MobileScaleCard.Body>
          ))}
        </MobileScaleCard.Container>
      </MobileScaleCard.Provider>
    </Card>
  );
};

const ScaleCardContent: React.FC<{
  data: ScaleCardContentData;
}> = ({ data }) => {
  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  return (
    <Scale.ExpandableArea>
      <View
        data-id={"scale-expandable-area-container"}
        gap={"$5"}
        paddingTop={"$2"}
      >
        <View>
          <Scale.Card
            data-id="scale-accordions-card"
            variant="gray"
            padding={"$4"}
          >
            <Scale.Accordion
              data-id={"scales-accordion"}
              gap={"$1.5"}
              data={data.accordion}
            >
              {({ content, state }) => (
                <View data-id={"scale-accordion-content"} gap={"$1.5"}>
                  <Scale.State
                    fontFamily={"$body"}
                    fontWeight={"$6"}
                    fontSize={"$6"}
                    lineHeight={"$6"}
                    textAlign="left"
                    state={state}
                    display={content.title ? "flex" : "none"}
                  >
                    {content.title}
                  </Scale.State>
                  <Text
                    fontFamily={"$body"}
                    fontWeight={"$6"}
                    lineHeight={"$6"}
                    display={content.description ? "flex" : "none"}
                  >
                    {content.description}
                  </Text>
                  <RenderMd md={content.mdText} />
                </View>
              )}
            </Scale.Accordion>
          </Scale.Card>
        </View>

        <Scale.Card
          data-id="scale-accordions-chart"
          variant="gray"
          padding={"$4"}
          display={data.chart.data.length > 1 ? "flex" : "none"}
        >
          <Scale.Card
            gap={"$3"}
            position="relative"
            borderWidth={"$0.25"}
            borderColor={"$gray-200"}
          >
            <ChartContainer
              data-id={"scale-chart-container"}
              height={362}
              config={data.chart.config}
            >
              <LineChart
                data-id={"scale-chart"}
                accessibilityLayer
                data={data.chart.data}
                margin={{
                  left: -15,
                  right: 30,
                  top: 12,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  data-id={"scale-chart-grid"}
                  strokeDasharray={"3 3"}
                  stroke="#CBCBCC"
                  vertical={false}
                />
                <XAxis
                  data-id={"scale-chart-x-axis"}
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={24}
                  tick={{
                    fill: "#636668",
                    fontFamily: "Fixel Text",
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                  tickFormatter={(_, index: number) => {
                    return `Чекап ${index + 1}`;
                  }}
                />
                <YAxis
                  data-id={"scale-chart-y-axis"}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={16}
                  tick={(props: TickProps) => <YAxisTick {...props} />}
                />
                <Tooltip
                  data-id={"scale-chart-tooltip"}
                  cursor={{
                    stroke: "#CBCBCC",
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  content={
                    <ChartTooltip.Content
                      data-id={"scale-chart-tooltip-content"}
                    >
                      {(payload, _, date) => (
                        <ChartTooltip.Container
                          data-id={"scale-chart-tooltip-container"}
                        >
                          <ChartTooltip.Title
                            data-id={"scale-chart-tooltip-title"}
                          >
                            {toReadableDate(date)}
                          </ChartTooltip.Title>
                          <View
                            data-id={"scale-chart-tooltip-items"}
                            gap={"$0.5"}
                          >
                            {payload?.map(({ color, value, config }, i) => (
                              <XStack
                                data-id={`scale-chart-tooltip-item`}
                                data-value={value}
                                gap={"$0.5"}
                                alignItems="center"
                                key={i}
                              >
                                <Circle
                                  backgroundColor={"white"}
                                  borderWidth={"$0.3"}
                                  borderColor={color}
                                  size={"$2"}
                                />

                                <Text
                                  data-id={`scale-chart-tooltip-value`}
                                  data-value={value}
                                  fontFamily={"$body"}
                                  fontSize={"$1"}
                                  color={"black"}
                                >
                                  {config?.label} {value}
                                </Text>
                              </XStack>
                            ))}
                          </View>
                        </ChartTooltip.Container>
                      )}
                    </ChartTooltip.Content>
                  }
                />
                {getChartLines(data.chart.config).map(
                  ({ dataKey, color }, i) => (
                    <Line
                      data-id={`scale-chart-line`}
                      data-key={dataKey}
                      key={i}
                      dataKey={dataKey}
                      type="linear"
                      stroke={color}
                      strokeWidth={2.5}
                      dot={{
                        r: 8,
                        strokeWidth: 2.5,
                        stroke: color,
                        fill: "white",
                      }}
                      activeDot={{
                        r: 3,
                        strokeWidth: 2.5,
                        stroke: color,
                        fill: color,
                      }}
                    />
                  ),
                )}
              </LineChart>
            </ChartContainer>
          </Scale.Card>
        </Scale.Card>
      </View>
    </Scale.ExpandableArea>
  );
};

const ScaleCardContentMobile: React.FC<{
  data: ScaleCardContentData;
}> = ({ data }) => {
  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  return (
    <MobileScaleCard.ExpandableArea>
      <View gap={"$3"} paddingTop={"$3"}>
        <Card variant="gray" padding={"$1"} borderRadius={"$7"}>
          <MobileScaleCard.Accordion gap={"$1"} data={data.accordion}>
            {({ content, state }) => (
              <View data-id={"scale-accordion-content"} gap={"$1.5"}>
                <Scale.State
                  fontFamily={"$body"}
                  fontWeight={"$6"}
                  fontSize={"$6"}
                  lineHeight={"$6"}
                  textAlign="left"
                  state={state}
                  display={content.title ? "flex" : "none"}
                >
                  {content.title}
                </Scale.State>
                <Text
                  fontFamily={"$body"}
                  fontWeight={"$6"}
                  lineHeight={"$3"}
                  display={content.description ? "flex" : "none"}
                >
                  {content.description}
                </Text>
                <RenderMd md={content.mdText} />
              </View>
            )}
          </MobileScaleCard.Accordion>
        </Card>

        <Card
          variant="gray"
          padding={"$1"}
          paddingTop={"$2"}
          borderRadius={"$7"}
          display={data.chart.data.length > 1 ? "flex" : "none"}
        >
          <View position="relative">
            <ChartContainer height={362} config={data.chart.config}>
              <LineChart
                accessibilityLayer
                data={data.chart.data}
                margin={{ left: -15, right: 30, top: 12, bottom: 24 }}
              >
                <CartesianGrid
                  strokeDasharray={"3 3"}
                  stroke="#CBCBCC"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  tick={{
                    fill: "#636668",
                    fontFamily: "Fixel Text",
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                  tickFormatter={(_, index) => {
                    return `Чекап ${index + 1}`;
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={16}
                  tick={(props: TickProps) => <YAxisTick {...props} />}
                />
                <Tooltip
                  cursor={{
                    stroke: "#CBCBCC",
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  content={
                    <ChartTooltip.Content>
                      {(payload, _, date) => (
                        <ChartTooltip.Container>
                          <ChartTooltip.Title>
                            {toReadableDate(date)}
                          </ChartTooltip.Title>
                          <View gap={"$0.5"}>
                            {payload?.map(({ color, value, config }, i) => (
                              <XStack gap={"$0.5"} alignItems="center" key={i}>
                                <Circle
                                  backgroundColor={"white"}
                                  borderWidth={"$0.3"}
                                  borderColor={color}
                                  size={"$2"}
                                />

                                <Text
                                  fontFamily={"$body"}
                                  fontSize={"$1"}
                                  color={"black"}
                                >
                                  {config?.label} {value}
                                </Text>
                              </XStack>
                            ))}
                          </View>
                        </ChartTooltip.Container>
                      )}
                    </ChartTooltip.Content>
                  }
                />
                {getChartLines(data.chart.config).map(
                  ({ dataKey, color }, i) => (
                    <Line
                      key={i}
                      dataKey={dataKey}
                      type="linear"
                      stroke={color}
                      strokeWidth={2.5}
                      dot={{
                        r: 8,
                        strokeWidth: 2.5,
                        stroke: color,
                        fill: "white",
                      }}
                      activeDot={{
                        r: 3,
                        strokeWidth: 2.5,
                        stroke: color,
                        fill: color,
                      }}
                    />
                  ),
                )}
              </LineChart>
            </ChartContainer>
          </View>
        </Card>
      </View>
    </MobileScaleCard.ExpandableArea>
  );
};
